import React, { useCallback, useEffect, useRef, useState } from 'react';

import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import classes from './LoadingSteps.module.scss';

export interface Phase {
    /** The main header text displayed for this phase */
    header: string;
    /** The description text displayed below the header */
    text: string;
    /** Duration in milliseconds. Use -1 for indefinite (typically the last phase) */
    duration: number;
}

interface Props {
    /** When true, phases progress normally based on duration. When false, triggers skip animation to completion. */
    isLoading: boolean;
    /** Array of phases to cycle through */
    phases: Phase[];
    /**
     * The minimum phase index that must be reached before onComplete can be called.
     * If isLoading becomes false before reaching this phase, the component will
     * fast-forward through phases until this phase is reached, then call onComplete.
     *
     * @default 8 (phase 9 in 1-based counting)
     * @example
     * // With 12 phases, minPhaseBeforeComplete=8, and skipStep=2:
     * // If data arrives at phase 3, it will skip: 3 → 5 → 7 → 9, then complete
     */
    minPhaseBeforeComplete?: number;
    /**
     * Number of phases to skip at a time during fast-forward mode.
     * When isLoading becomes false before reaching minPhaseBeforeComplete,
     * the component advances by this many phases every 1 second.
     *
     * @default 2 (skips every other phase)
     * @example
     * // skipStep=2: phase 3 → 5 → 7 → 9
     * // skipStep=3: phase 3 → 6 → 9
     * // skipStep=1: phase 3 → 4 → 5 → 6 → 7 → 8 → 9
     */
    skipStep?: number;
    /** Called when the loading animation completes. Use this to show your content. */
    onComplete: () => void;
}

export const LoadingSteps = ({ isLoading, phases, minPhaseBeforeComplete = 8, skipStep = 2, onComplete }: Props) => {
    const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
    const [completedHeaders, setCompletedHeaders] = useState<string[]>([]);
    const [isVisible, setIsVisible] = useState(true);
    const [isSkipping, setIsSkipping] = useState(false);

    const onCompleteCalledRef = useRef(false);
    const prevPhaseHeaderRef = useRef<string | null>(null);

    const currentPhase = phases[currentPhaseIndex];

    // Track completed headers when phase changes
    // Headers might repeat, so only add to completedHeaders if different from previous
    useEffect(() => {
        if (!currentPhase) return;

        const prevHeader = prevPhaseHeaderRef.current;
        if (prevHeader && prevHeader !== currentPhase.header) {
            setCompletedHeaders((prev) => (prev.includes(prevHeader) ? prev : [...prev, prevHeader]));
        }
        prevPhaseHeaderRef.current = currentPhase.header;
    }, [currentPhase]);

    // Handle completion
    const handleComplete = useCallback(() => {
        if (onCompleteCalledRef.current) return;
        onCompleteCalledRef.current = true;
        setIsVisible(false);
        onComplete();
    }, [onComplete]);

    // Detect isLoading becoming false - start skipping or complete
    useEffect(() => {
        if (!isLoading && !isSkipping && !onCompleteCalledRef.current) {
            if (currentPhaseIndex >= minPhaseBeforeComplete) {
                handleComplete();
            } else {
                setIsSkipping(true);
            }
        }
    }, [currentPhaseIndex, handleComplete, isLoading, isSkipping, minPhaseBeforeComplete]);

    // Normal phase progression
    useEffect(() => {
        if (!isLoading || isSkipping) return;
        if (currentPhase?.duration === -1) return; // Indefinite, don't auto-advance

        const timeout = setTimeout(() => {
            if (currentPhaseIndex < phases.length - 1) {
                setCurrentPhaseIndex((prev) => prev + 1);
            }
        }, currentPhase?.duration || 4000);

        return () => clearTimeout(timeout);
    }, [isLoading, isSkipping, currentPhaseIndex, currentPhase?.duration, phases.length]);

    // Skip mode: jump phases every 1s until reaching minPhaseBeforeComplete
    useEffect(() => {
        if (!isSkipping) return;

        const interval = setInterval(() => {
            setCurrentPhaseIndex((prev) => {
                const next = Math.min(prev + skipStep, phases.length - 1);
                if (next >= minPhaseBeforeComplete) {
                    // Arrived to last step, clear interval and show for 2 seconds last step.
                    clearInterval(interval);
                    setTimeout(handleComplete, 2000);
                }
                return next;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [handleComplete, isSkipping, minPhaseBeforeComplete, phases.length, skipStep]);

    if (!isVisible || !currentPhase) return null;

    return (
        <div className={classes.loadingSteps}>
            {completedHeaders.map((header) => (
                <div key={header} className={classes.step}>
                    <div className={`${classes.icon} ${classes.checkIcon}`}>
                        <MuiIcon icon={'check_circle'} type={'outlined'} fontSize={'2.1rem'} color={'inherit'} />
                    </div>
                    <div className={classes.content}>
                        <div className={`${classes.header} ${classes.completed}`}>{header}</div>
                    </div>
                </div>
            ))}

            <div className={classes.step}>
                <div className={`${classes.icon} ${classes.pulseIndicator}`} />
                <div className={classes.content}>
                    <div className={`${classes.header} ${classes.active}`}>{currentPhase.header}</div>
                    <div className={classes.text}>{currentPhase.text}</div>
                </div>
            </div>
        </div>
    );
};
